import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, Input, Select, SkeletonStack, Slider, Switch } from '@/shared/ui'
import { useSaveVoice, useVoiceSettings } from '@/features/voice-edit/model'
import type { ModelChoice, VoiceSettings } from '@/shared/api/types'
import styles from './VoicePage.module.css'

/** Модели синтеза. Разница между ними слышна сразу, поэтому подписана. */
/**
 * Список моделей приходит с сервера вместе с настройками.
 *
 * Раньше он лежал копией прямо здесь и отставал: gpt-4.1-mini добавили в
 * отладочную панель, а клиент её не видел, и подписи вроде «самая
 * быстрая» давно разошлись с замерами. Один источник — один список.
 */
function toOptions(choices: ModelChoice[] | undefined) {
  return [
    { value: '', label: 'Как настроено у нас' },
    ...(choices ?? []).map((c) => ({
      value: c.model,
      label: c.note ? `${c.label} — ${c.note}` : c.label,
    })),
  ]
}


/**
 * Подача голоса компании.
 *
 * Настройки, которые раньше крутились в отладочной панели. Переезд в
 * кабинет означает, что голос — часть продукта клиента: одному нужен
 * ровный диспетчер, другому живой продавец, и одним значением на всех
 * это не покрыть.
 */
export function VoicePage() {
  const voice = useVoiceSettings()

  return (
    <>
      {voice.isPending ? (
        <Card>
          <SkeletonStack rows={6} height={40} />
        </Card>
      ) : voice.data ? (
        <VoiceForm key={voice.data.updatedAt} initial={voice.data} />
      ) : null}
    </>
  )
}

function VoiceForm({ initial }: { initial: VoiceSettings }) {
  const [form, setForm] = useState<VoiceSettings>(initial)
  const save = useSaveVoice()

  const dirty = JSON.stringify(form) !== JSON.stringify(initial)

  function set<K extends keyof VoiceSettings>(key: K, value: VoiceSettings[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function submit() {
    // Отправляем только изменённое: сервер накладывает присланные поля
    // на текущие, и лишние поля в запросе — лишний способ затереть то,
    // чего человек не трогал.
    const patch: Partial<VoiceSettings> = {}
    for (const key of Object.keys(form) as (keyof VoiceSettings)[]) {
      if (key === 'updatedAt') continue
      if (form[key] !== initial[key]) {
        Object.assign(patch, { [key]: form[key] })
      }
    }
    save.mutate(patch)
  }

  return (
    <>
      <Section
        title="Подача"
        action={
          <Link className={styles.link} to="/test-call">
            Проверить звонком
          </Link>
        }
      >
        <Row
          label="Живость"
          hint="Ниже — интонации ярче, но одна и та же фраза звучит по-разному от раза к разу. Выше — предсказуемо и монотонно."
        >
          <Slider
            label="Живость"
            value={form.stability}
            min={0}
            max={1}
            step={0.05}
            hideLabel
            ends={['выразительнее', 'ровнее']}
            onChange={(v) => set('stability', v)}
            format={(v) => v.toFixed(2)}
          />
        </Row>

        <Row label="Похожесть на диктора" hint="Насколько держаться тембра исходного голоса.">
          <Slider
            label="Похожесть"
            value={form.similarity}
            min={0}
            max={1}
            step={0.05}
            hideLabel
            ends={['свободнее', 'ближе к оригиналу']}
            onChange={(v) => set('similarity', v)}
            format={(v) => v.toFixed(2)}
          />
        </Row>

        <Row
          label="Темп речи"
          hint="Единица — обычная скорость. Выше 1.2 агент начинает глотать окончания."
        >
          <Slider
            label="Темп"
            value={form.speed}
            min={0.5}
            max={1.5}
            step={0.05}
            hideLabel
            ends={['медленнее', 'быстрее']}
            onChange={(v) => set('speed', v)}
            format={(v) => `${v.toFixed(2)}×`}
          />
        </Row>

        <Row
          label="Выразительность"
          hint="Поддерживается не всеми моделями. На flash 2.5 не действует."
        >
          <Slider
            label="Выразительность"
            value={form.style}
            min={0}
            max={1}
            step={0.05}
            hideLabel
            ends={['нейтрально', 'эмоционально']}
            onChange={(v) => set('style', v)}
            format={(v) => v.toFixed(2)}
          />
        </Row>
      </Section>

      <Section title="Перебивание">
        <Row
          label="Уступка"
          hint="Живой человек замолкает примерно за четверть секунды. Ноль даёт щелчок, больше полусекунды — собеседник дольше слышит уже ненужное."
        >
          <Slider
            label="Уступка"
            value={form.fadeMs}
            min={0}
            max={800}
            step={10}
            hideLabel
            ends={['обрывать резко', 'уступать плавно']}
            onChange={(v) => set('fadeMs', v)}
            format={(v) => `${v} мс`}
          />
        </Row>

        <Row
          label="Форма уступки"
          hint="Больше единицы — громкость падает сразу, а тихий хвост тянется. Так собеседник быстрее слышит, что его услышали."
        >
          <Slider
            label="Форма"
            value={form.fadeShape}
            min={1}
            max={6}
            step={0.1}
            hideLabel
            ends={['ровно', 'резкий спад']}
            onChange={(v) => set('fadeShape', v)}
            format={(v) => v.toFixed(1)}
          />
        </Row>
      </Section>

      <Section title="Модели">
        <Row label="Синтез речи" hint="Пусто — берётся то, что настроено у нас">
          <Select
            options={toOptions(initial.ttsChoices)}
            value={form.ttsModel}
            onChange={(e) => set('ttsModel', e.target.value)}
          />
        </Row>

        <Row label="Диалоговая модель" hint="Та, что придумывает ответы на ходу">
          <Select
            options={toOptions(initial.llmChoices)}
            value={form.llmModel}
            onChange={(e) => set('llmModel', e.target.value)}
          />
        </Row>

        <Row label="Голос" hint="Идентификатор голоса ElevenLabs. Пусто — наш по умолчанию">
          <Input
            placeholder="напр. 21m00Tcm4TlvDq8ikWAM"
            value={form.voiceId}
            onChange={(e) => set('voiceId', e.target.value)}
          />
        </Row>

        <Row
          label="Нарезка речи"
          hint="Прямой размен: цельная реплика звучит связнее, но начинает звучать позже"
        >
          <Select
            options={toOptions(initial.chunkChoices)}
            value={form.chunking}
            onChange={(e) => set('chunking', e.target.value)}
          />
        </Row>
      </Section>

      <Section title="Линия">
        <Row
          label="Имитировать телефонную линию"
          hint="На реальном звонке узкая полоса и шум получаются сами. В браузере тракт чистый, и голос звучит иначе, чем услышит собеседник."
        >
          <Switch
            checked={form.phoneFx}
            onChange={(e) => set('phoneFx', e.target.checked)}
            label={form.phoneFx ? 'включена' : 'выключена'}
          />
        </Row>

        {form.phoneFx && (
          <Row label="Перегруз голоса" hint="Насколько грубо звучит линия">
            <Slider
              label="Перегруз"
              value={form.phoneDrive}
              min={1}
              max={12}
              step={0.5}
              ends={['чисто', 'жёстко']}
              onChange={(v) => set('phoneDrive', v)}
              format={(v) => v.toFixed(1)}
            />
          </Row>
        )}

        <Row
          label="Готовить текст к произношению"
          hint="Латиница читается кириллицей, тире — словами, лишние теги вычищаются. Выключать стоит только при разборе, почему агент произносит что-то не так."
        >
          <Switch
            checked={form.textNorm}
            onChange={(e) => set('textNorm', e.target.checked)}
            label={form.textNorm ? 'включено' : 'выключено'}
          />
        </Row>

        <Row
          label="Перебивание по словам"
          hint="Экспериментально. Агент замолкает не от любого звука, а когда в нём распознались слова: шорохи и стук не перебивают, «подождите» — перебивает. Добавляет до полсекунды к реакции на перебивание."
        >
          <Switch
            checked={form.bargeInConfirm}
            onChange={(e) => set('bargeInConfirm', e.target.checked)}
            label={form.bargeInConfirm ? 'включено' : 'выключено'}
          />
        </Row>
      </Section>

      <div className={styles.actions}>
        <Button onClick={submit} loading={save.isPending} disabled={!dirty}>
          Сохранить
        </Button>
        {dirty && <span className={styles.dirty}>Есть несохранённые правки</span>}
      </div>
    </>
  )
}

/** Раздел настроек: заголовок-полоска и строки под ним. */
function Section({
  title,
  action,
  children,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>{title}</span>
        {action}
      </div>
      {children}
    </div>
  )
}

/** Одна настройка: подпись с пояснением слева, регулятор справа. */
function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <div>
        <div className={styles.label}>{label}</div>
        {hint && <div className={styles.hint}>{hint}</div>}
      </div>
      <div className={styles.control}>{children}</div>
    </div>
  )
}
